import React, { useState, useEffect, useCallback, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { BadgeStatus, VehicleType, DATE_FORMAT, RequestStatus, RoutePath, EntityType, IAppUserProfile } from "shared/constants";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { requestPending, requestPristine } from "state/reducer/requestStatusSlice";
import axios from "axios";
import { getVehicles } from "net/contracts";
import { Button, Table } from "@ds/ds-snam/react";
import TableRighArrow from "components/TableRightArrow";
import { v4 as uuid } from "uuid";
import useRequestStatus from "hooks/useRequestStatus";
import moment from "moment";
import SimpleTable, { SimpleTableType } from "components/SimpleTable";
import NoDataFound from "components/NoDataFound";
import { getBadgeStatus } from "views/contractView/shared/driverVehicleBadgeStatus";
import DsBadge from "components/DsBadge";
import DriverVehicleListTableLegend from "./components/driverVehicleListTableLegend";
import "views/contractView/styles/contractDataStyles.css";
import AddEntityButton from "components/AddEntityButton";
import useAppUser from "hooks/useAppUser";
import { IVehicleElement } from "dataModels/contract";
import { getVehicleRows } from "./shared/listUtils";
import { ITableConfig, ITableRow } from "@ds/ds-snam/dist/models/tables";
import { IParseDateFormatType, parseDateForDS } from "shared/utils/dateUtils";
import { getDSTableShaderElementsForFilterColumn,getDSTableSharedElements, getTableCellsHeaderSimple  } from "shared/utils/dsComponentUtils";


const VehicleList: React.FC<{}> = (props): JSX.Element => {
    const { t } = useTranslation(['contract', 'app']);
    const appUserProfile:IAppUserProfile | undefined = useAppUser()?.userProfile;

    const [vehicleList, setVehicleList] = useState<
        {
            [BadgeStatus.TO_CREDIT]: IVehicleElement[],
            [BadgeStatus.CREDITED]: IVehicleElement[]
        }
    >();
    const [vehicleListFiltered, setVehicleListFiltered] = useState<
        {
            [BadgeStatus.TO_CREDIT]: IVehicleElement[],
            [BadgeStatus.CREDITED]: IVehicleElement[]
        }
    >();
    const [filtersAwaiting, setFiltersAwaiting] = useState<{ [key: string]: string }>({
        col1: "",
        col2: "",
    });
    const [filtersCredited, setFiltersCredited] = useState<{ [key: string]: string }>({
        col1: "",
        col2: ""
    });   

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const requestStatus = useRequestStatus();

    const loadVehicles = useCallback(() => {
        dispatch(requestPending());
        const toCreditReq = getVehicles(BadgeStatus.TO_CREDIT);
        const creditedReq = getVehicles(BadgeStatus.CREDITED);

        axios.all([toCreditReq, creditedReq])
            .then(axios.spread((...responses) => {
                //console.log(responses)
                setVehicleListFiltered({
                    [BadgeStatus.TO_CREDIT]: responses[0],
                    [BadgeStatus.CREDITED]: responses[1]
                });
                setVehicleList({
                    [BadgeStatus.TO_CREDIT]: responses[0],
                    [BadgeStatus.CREDITED]: responses[1]
                });
            }))
            .catch((error) => {
                console.log(error)
            })
            .finally(() => {
                dispatch(requestPristine())
            })
    }, [dispatch]);

    useEffect(() => {
        loadVehicles()
        return () => {
            dispatch(requestPristine());
        }
    }, [loadVehicles, dispatch]);

    const getColumns = (status: BadgeStatus) => {
        const translationSuffix: string = (status === BadgeStatus.TO_CREDIT) ? "notCredited" : "credited";
        return t("vehicle.vehicleList.tableColumns." + translationSuffix, { ns: 'contract', returnObjects: true }) as string[];
    }

    const getVehicleListSearchColumnsAwaiting = (index: number) => {
        return index === 0 || index === 1 || index === 2; 
    };
    
    const getVehicleListSearchColumnsCredited = (index: number) => {
        return index === 0 || index === 1 || index === 2; 
    };

    // Gestione dei cambiamenti dei filtri
    const handleFilterChangeAwaiting = (columnKey: string, value: string) => {
        setFiltersAwaiting((prevFilters) => ({
            ...prevFilters,
            [columnKey]: value,
        }));
    };
    // Gestione dei cambiamenti dei filtri
    const handleFilterChangeCredited = (columnKey: string, value: string) => {
        setFiltersCredited((prevFilters) => ({
            ...prevFilters,
            [columnKey]: value,
        }));
    };
    
    useEffect(() => {
        if (vehicleList) {
            setVehicleListFiltered({
                [BadgeStatus.TO_CREDIT]: vehicleList[BadgeStatus.TO_CREDIT].filter(vehicle =>
                    //vehicle.type.toLowerCase().includes(filterColumn0Awaiting.toLowerCase()) &&
                    vehicle.licencePlate.toLowerCase().includes(filtersAwaiting.col2.toLowerCase())&&
                    t(`vehicle.vehicleList.vehicleType.${vehicle.type}`).toLowerCase().includes(filtersAwaiting.col1.toLowerCase())
                ),
                [BadgeStatus.CREDITED]: vehicleList[BadgeStatus.CREDITED].filter(vehicle =>
                    //vehicle.firstName.toLowerCase().includes(filterColumn0Credited.toLowerCase()) &&
                    vehicle.licencePlate.toLowerCase().includes(filtersCredited.col2.toLowerCase())&&
                    t(`vehicle.vehicleList.vehicleType.${vehicle.type}`).toLowerCase().includes(filtersCredited.col1.toLowerCase())
                ),
            });
        }
    }, [filtersAwaiting,filtersCredited]);

    const [sortOrderAwaiting, setSortOrderAwaiting] = useState<'asc' | 'desc' | null>(null);
    const [sortOrderCredited, setSortOrderCredited] = useState<'asc' | 'desc' | null>(null);

    const handleSortColumn2Awaiting = () => {
    if (vehicleListFiltered) {
        const sortedList = [...vehicleListFiltered[BadgeStatus.TO_CREDIT]].sort((a, b) => {
            const dateA = moment(a.date * 1000); // Data di inizio per il primo veicolo
            const dateB = moment(b.date * 1000); // Data di inizio per il secondo veicolo

            // Se sortOrderAwaiting è null, applica il primo ordinamento in ordine decrescente
            if (sortOrderAwaiting === null) {
                return dateB.diff(dateA); // Ordinamento decrescente
            }

            // Alterna tra crescente e decrescente
            return sortOrderAwaiting === 'asc'
                ? dateA.diff(dateB) // Ordinamento crescente
                : dateB.diff(dateA); // Ordinamento decrescente
        });

        setVehicleListFiltered((prev) => ({
            [BadgeStatus.TO_CREDIT]: sortedList,
            [BadgeStatus.CREDITED]: prev?.[BadgeStatus.CREDITED] || [], // Mantieni la lista accreditata invariata
        }));

        // Alterna l'ordinamento per la colonna 2
        setSortOrderAwaiting(prev => prev === null || prev === 'desc' ? 'asc' : 'desc');
    }
};
        
const handleSortColumn2Credited = () => {
    if (vehicleListFiltered) {
        const sortedList = [...vehicleListFiltered[BadgeStatus.CREDITED]].sort((a, b) => {
            const dateA = moment(a.date * 1000); // Data di inizio per il primo veicolo
            const dateB = moment(b.date * 1000); // Data di inizio per il secondo veicolo

            // Se sortOrderCredited è null, applica il primo ordinamento in ordine decrescente
            if (sortOrderCredited === null) {
                return dateB.diff(dateA); // Ordinamento decrescente
            }

            // Alterna tra crescente e decrescente
            return sortOrderCredited === 'asc'
                ? dateA.diff(dateB) // Ordinamento crescente
                : dateB.diff(dateA); // Ordinamento decrescente
        });

        setVehicleListFiltered((prev) => ({
            [BadgeStatus.TO_CREDIT]: prev?.[BadgeStatus.TO_CREDIT] || [], // Mantieni la lista non accreditata invariata
            [BadgeStatus.CREDITED]: sortedList,
        }));

        // Alterna l'ordinamento per la colonna 2
        setSortOrderCredited(prev => prev === null || prev === 'desc' ? 'asc' : 'desc');
    }
};

      const getTableRowsToCredit = (): ITableRow[] => {
            let myRetArr: ITableRow[] = [];
            if(vehicleListFiltered!==undefined){
              for (let i in vehicleListFiltered[BadgeStatus.TO_CREDIT]) {
                  let tempObj: ITableRow = {
                      tableCells: [
                          { 
                              type: "text-badge", 
                              text: t(`vehicle.vehicleList.vehicleType.${vehicleListFiltered[BadgeStatus.TO_CREDIT][i].type}`),
                              badgeStatus: (vehicleListFiltered[BadgeStatus.TO_CREDIT][i].status === BadgeStatus.EXPIRED||vehicleListFiltered[BadgeStatus.TO_CREDIT][i].status === BadgeStatus.NOTAPPROVED||vehicleListFiltered[BadgeStatus.TO_CREDIT][i].status === BadgeStatus.NOT_CREDITED) ?
                              "error":
                              ( vehicleListFiltered[BadgeStatus.TO_CREDIT][i].status === BadgeStatus.CREDITED?
                                "confirm":
                                "warning"
                              )
                          },
                          { 
                              type: "text", 
                              text: vehicleListFiltered[BadgeStatus.TO_CREDIT][i].licencePlate,
                          },
                          { 
                              type: "text", 
                              text: parseDateForDS(moment.unix(vehicleListFiltered[BadgeStatus.TO_CREDIT][i].date).format(DATE_FORMAT),IParseDateFormatType.DATE_FORMAT1),
                          },
                        { 
                            type: "text", 
                            text: vehicleListFiltered[BadgeStatus.TO_CREDIT][i].transporterName?vehicleListFiltered[BadgeStatus.TO_CREDIT][i].transporterName:"",
                        }
                      ],
                      rightIcon: "icon-chevron-right",
                  };
                  myRetArr.push(tempObj);
              }
            }
            return myRetArr;
      };
      const getTableRowsCredited = (): ITableRow[] => {
        let myRetArr: ITableRow[] = [];
        if(vehicleListFiltered!==undefined){
          for (let i in vehicleListFiltered[BadgeStatus.CREDITED]) {
              let tempObj: ITableRow = {
                  tableCells: [
                      { 
                          type: "text-badge", 
                          text: t(`vehicle.vehicleList.vehicleType.${vehicleListFiltered[BadgeStatus.CREDITED][i].type}`),
                          badgeStatus: (vehicleListFiltered[BadgeStatus.CREDITED][i].status === BadgeStatus.EXPIRED||vehicleListFiltered[BadgeStatus.CREDITED][i].status === BadgeStatus.NOTAPPROVED||vehicleListFiltered[BadgeStatus.CREDITED][i].status === BadgeStatus.NOT_CREDITED) ?
                          "error":
                          ( vehicleListFiltered[BadgeStatus.CREDITED][i].status === BadgeStatus.APPROVED?
                            "confirm":
                            "warning"
                          )
                      },
                      { 
                          type: "text", 
                          text: vehicleListFiltered[BadgeStatus.CREDITED][i].licencePlate,
                      },
                      { 
                          type: "text", 
                          text: parseDateForDS(moment.unix(vehicleListFiltered[BadgeStatus.CREDITED][i].date).format(DATE_FORMAT),IParseDateFormatType.DATE_FORMAT1),
                      },
                      { 
                          type: "text", 
                          text: vehicleListFiltered[BadgeStatus.CREDITED][i].transporterName?vehicleListFiltered[BadgeStatus.CREDITED][i].transporterName:"",
                      }
                  ],
                  rightIcon: "icon-chevron-right",
              };
              myRetArr.push(tempObj);
          }
        }
        return myRetArr;
  };


  
      const myTableConfigToCredit: ITableConfig = {
              ...getDSTableSharedElements(280),
              tableHeader: {
                  
                  tableCellsHeader: [
                      {
                        ...getDSTableShaderElementsForFilterColumn(t("vehicle.vehicleList.tableColumns.notCredited.0", { ns: 'contract' }) as string,"col1","25%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                        searchBoxValue:filtersAwaiting["col1"], //<---
                      },
                      {
                        ...getDSTableShaderElementsForFilterColumn(t("vehicle.vehicleList.tableColumns.notCredited.1", { ns: 'contract' }) as string,"col2","25%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                        searchBoxValue:filtersAwaiting["col2"], //<---
                      },
                      getTableCellsHeaderSimple(t("vehicle.vehicleList.tableColumns.notCredited.2", { ns: 'contract' }) as string,"col3","25%","left",true),
                      getTableCellsHeaderSimple(t("vehicle.vehicleList.tableColumns.notCredited.4", { ns: 'contract' }) as string,"col4","25%","left",false),
                  ],
                  hasSearchBox: true,
              },
              tableRows: getTableRowsToCredit()
          };
    const myTableConfigCredited: ITableConfig = {
            ...getDSTableSharedElements(280),
            tableHeader: {
                
                tableCellsHeader: [
                    {
                        ...getDSTableShaderElementsForFilterColumn(t("vehicle.vehicleList.tableColumns.credited.0", { ns: 'contract' }) as string,"col1","25%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                        searchBoxValue:filtersCredited["col1"], //<---
                    },
                    {
                        ...getDSTableShaderElementsForFilterColumn(t("vehicle.vehicleList.tableColumns.credited.1", { ns: 'contract' }) as string,"col2","25%",t('general.buttons.cancel', { ns: 'app' }),t('general.buttons.confirm', { ns: 'app' }),t('general.buttons.search', { ns: 'app' })),
                        searchBoxValue:filtersCredited["col2"], //<---
                    },
                    getTableCellsHeaderSimple(t("vehicle.vehicleList.tableColumns.credited.2", { ns: 'contract' }) as string,"col3","25%","left",true),
                    getTableCellsHeaderSimple(t("vehicle.vehicleList.tableColumns.credited.4", { ns: 'contract' }) as string,"col4","25%","left",false),
                ],
                hasSearchBox: true,
            },
            tableRows: getTableRowsCredited()
        };
    return <>
        <p className="pageTitle" style={{ marginBottom: "10px" }}>
            {t('vehicle.vehicleList.title')}
        </p>
        {/*<p className="pageSubTitle">
            {t('vehicle.vehicleList.subTitle')}
        </p>*/}

        {appUserProfile?.addEntityPolicy.includes(EntityType.VEHICLE)&&
            <div style={{ marginTop: "28px", marginBottom: "14px", display: "flex", alignItems: "flex-start", justifyContent: "flex-end" }}>
                <AddEntityButton entityType={EntityType.VEHICLE} />
            </div>
        }


        {/*Componenti in attesa di accreditamento*/}
        {vehicleList && vehicleList[BadgeStatus.TO_CREDIT].length && requestStatus === RequestStatus.PRISTINE ?
            <>
                <p className="driverAndVehicleListTableTitle">
                    {t('vehicle.vehicleList.tableTitle.notCredited')}
                </p>
                {
                    <Table 
                        tableConfig={myTableConfigToCredit} 
                        style={{}}
                        onInputSearchBarChange={(event:Event)=>{
                            console.log((event as CustomEvent).detail.searchedText + " - " + (event as CustomEvent).detail.cells)
                        }}
                        onClickRightAction={(event:Event)=>{
                            //console.log(event)
                            const driverId = vehicleListFiltered?.[BadgeStatus.TO_CREDIT][(event as CustomEvent).detail.row].vehicleId;
                            if (driverId) {
                                navigate(driverId);
                            }
                        }}
                        //FILTERS
                        onSubmitSearchedText={(event:Event)=>{
                                const focusColtemp = (event as CustomEvent).detail.cells;
                                let focusCol = '';
                                switch (focusColtemp) {
                                    case 0:
                                        focusCol = "col1";
                                        break;
                                    case 1:
                                        focusCol = "col2";
                                        break;
                                    default:
                                        break;
                                }
                                const focusValue = (event as CustomEvent).detail.searchedText;
                                handleFilterChangeAwaiting(focusCol, focusValue)
                            }}
                        onTableInputChange={(event:Event)=>{
                            //console.log(event)
                            const focusCol = (event as CustomEvent).detail.col;
                            const focusValue = (event as CustomEvent).detail.value;
                            //console.log(focusCol + " - " + focusValue)
                            //handleFilterChangeAwaiting(focusCol, focusValue)
                        }}
                        onColumnSorting={(event:Event)=>{
                            //console.log((event as CustomEvent).detail.columnID) 
                            switch((event as CustomEvent).detail.columnID){
                              case "col3":
                                handleSortColumn2Awaiting()
                                break;
                              default:  
                                break;
                            }
                        }}
                        onResetSearch={(event:Event)=>{
                            //console.log('cliccata cella: ' + (event as CustomEvent).detail.cells);
                            switch ((event as CustomEvent).detail.cells) {
                                case 0:
                                    handleFilterChangeAwaiting("col1", "");
                                    break;
                                case 1:
                                    handleFilterChangeAwaiting("col2", "");
                                    break;
                                default:
                                    break;
                            }
                        }}
                    />
                }
                <DriverVehicleListTableLegend entityType={EntityType.VEHICLE} tableType={BadgeStatus.NOT_CREDITED} />
            </> : <></>}


        {/*Componenti accreditati*/}
        {vehicleList && vehicleList[BadgeStatus.CREDITED].length && requestStatus === RequestStatus.PRISTINE ?
            <div style={{ marginTop: "36px" }}>
                <p className="driverAndVehicleListTableTitle">
                    {t('vehicle.vehicleList.tableTitle.credited')}
                </p>
                
                <div className="listTableContainer">
                {
                    <Table 
                        tableConfig={myTableConfigCredited} 
                        style={{}}
                        onInputSearchBarChange={(event:Event)=>{
                            console.log((event as CustomEvent).detail.searchedText + " - " + (event as CustomEvent).detail.cells)
                        }}
                        onClickRightAction={(event:Event)=>{
                            //console.log(event)
                            const driverId = vehicleListFiltered?.[BadgeStatus.CREDITED][(event as CustomEvent).detail.row].vehicleId;
                            if (driverId) {
                                navigate(driverId);
                            }
                        }}
                        //FILTERS
                        onSubmitSearchedText={(event:Event)=>{
                                const focusColtemp = (event as CustomEvent).detail.cells;
                                let focusCol = '';
                                switch (focusColtemp) {
                                    case 0:
                                        focusCol = "col1";
                                        break;
                                    case 1:
                                        focusCol = "col2";
                                        break;
                                    default:
                                        break;
                                }
                                const focusValue = (event as CustomEvent).detail.searchedText;
                                handleFilterChangeCredited(focusCol, focusValue)
                            }}
                        onTableInputChange={(event:Event)=>{
                            //console.log(event)
                            const focusCol = (event as CustomEvent).detail.col;
                            const focusValue = (event as CustomEvent).detail.value;
                            //console.log(focusCol + " - " + focusValue)
                            //handleFilterChangeCredited(focusCol, focusValue)
                        }}
                        onColumnSorting={(event:Event)=>{
                            //console.log((event as CustomEvent).detail.columnID) 
                            switch((event as CustomEvent).detail.columnID){
                              case "col3":
                                handleSortColumn2Credited()
                                break;
                              default:  
                                break;
                            }
                        }}
                        onResetSearch={(event:Event)=>{
                            //console.log('cliccata cella: ' + (event as CustomEvent).detail.cells);
                            switch ((event as CustomEvent).detail.cells) {
                                case 0:
                                    handleFilterChangeCredited("col1", "");
                                    break;
                                case 1:
                                    handleFilterChangeCredited("col2", "");
                                    break;
                                default:
                                    break;
                            }
                        }}
                    />
                }
                <DriverVehicleListTableLegend entityType={EntityType.VEHICLE} tableType={BadgeStatus.CREDITED} />
                </div>
            </div> : <></>}

        {vehicleList && (vehicleList[BadgeStatus.TO_CREDIT].length === 0 && vehicleList[BadgeStatus.CREDITED].length === 0) && requestStatus === RequestStatus.PRISTINE &&
            <NoDataFound title={t('vehicle.vehicleList.noData.title')} subTitle={t('vehicle.vehicleList.noData.subTitle')} />
        }

    </>

}

export default VehicleList;